import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { Eye, Edit3, Trash2, MapPin, Calendar, MoreVertical } from 'lucide-react';
import dayjs from '@/lib/dayjs';
import { Link } from 'react-router-dom';
import { formatNumber } from '@/utils';
import { StatusBadge } from '@/components/common';

interface JobTableProps {
  jobs: any[];
  onDelete: (id: string) => void;
}

export function JobTable({ jobs, onDelete }: JobTableProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, jobId: string) => {
    setAnchorEl(event.currentTarget);
    setActiveJobId(jobId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveJobId(null);
  };

  const activeJob = jobs.find((j) => j.id === activeJobId);

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: '24px',
        overflowX: 'auto',
        boxShadow: 'none',
        border: '1px solid #f1f3f5',
        '&::-webkit-scrollbar': {
          height: '6px',
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f8fafc',
          borderRadius: '999px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#cbd5e1',
          borderRadius: '999px',
          '&:hover': {
            backgroundColor: '#94a3b8',
          },
        },
      }}
    >
      <Table sx={{ minWidth: 1000 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #f1f3f5' }}>
            <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Tin tuyển dụng</TableCell>
            <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Ngành nghề</TableCell>
            <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Mức lương</TableCell>
            <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Địa điểm & Hạn nộp</TableCell>
            <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Số lượng</TableCell>
            <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Trạng thái</TableCell>
            <TableCell
              sx={{
                py: 2.5,
                px: 3,
                fontWeight: '700',
                color: '#64748b',
                fontSize: '11px',
                textTransform: 'uppercase',
                tracking: '0.05em',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                position: 'sticky',
                right: 0,
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #f1f3f5',
                zIndex: 2,
                boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.05)',
              }}
            >
              Thao tác
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job) => (
            <TableRow key={job.id} hover sx={{ '&:hover': { backgroundColor: '#fafafc !important' } }}>
              {/* Job Title & Company */}
              <TableCell sx={{ py: 2, px: 3 }}>
                <div>
                  <h4 className="font-bold text-gray-800 leading-snug hover:text-[#f97316] transition-colors line-clamp-1">
                    {job.title}
                  </h4>
                  {job.company && (
                    <span className="text-[11px] text-gray-400 font-semibold block mt-0.5">{job.company.name}</span>
                  )}
                </div>
              </TableCell>

              {/* Category */}
              <TableCell sx={{ py: 2, px: 3 }}>
                <span className="text-xs font-semibold text-gray-700">
                  {job.jobCategoryName || 'Khác'}
                </span>
              </TableCell>

              {/* Expected Salary */}
              <TableCell sx={{ py: 2, px: 3, fontWeight: 'bold', color: '#334155', fontSize: '13px' }}>
                {job.salaryMin && job.salaryMax
                  ? `${formatNumber(parseFloat(job.salaryMin))} - ${formatNumber(parseFloat(job.salaryMax))} ${job.currencyUnit || 'VNĐ'}`
                  : 'Thỏa thuận'}
              </TableCell>

              {/* Location & Deadline */}
              <TableCell sx={{ py: 2, px: 3 }}>
                <div className="space-y-1">
                  {job.workingLocation && (
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-600">
                      <MapPin size={12} className="text-[#f97316] shrink-0" />
                      <span className="line-clamp-1">{job.workingLocation}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-600">
                    <Calendar size={12} className="text-gray-400 shrink-0" />
                    <span>Hạn: {job.deadline ? dayjs(job.deadline).format('DD/MM/YYYY') : 'Không giới hạn'}</span>
                  </div>
                </div>
              </TableCell>

              {/* Vacancies */}
              <TableCell sx={{ py: 2, px: 3 }}>
                <div className="text-sm font-bold text-gray-700">{job.vacancies || 1} người</div>
              </TableCell>

              {/* Status Badge */}
              <TableCell sx={{ py: 2, px: 3 }}>
                <StatusBadge
                  status={job.approvalStatus || 'pending'}
                  label={job.approvalStatus === 'approved' ? 'Đang hiển thị' : job.approvalStatus === 'rejected' ? 'Từ chối' : 'Chưa duyệt'}
                />
              </TableCell>

              {/* Actions Column */}
              <TableCell
                sx={{
                  py: 2,
                  px: 3,
                  textAlign: 'center',
                  position: 'sticky',
                  right: 0,
                  backgroundColor: '#ffffff',
                  zIndex: 1,
                  boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.05)',
                }}
              >
                <div className="flex items-center justify-center gap-1.5">
                  <Tooltip title="Xem chi tiết" arrow>
                    <Link to={`/viec-lam/${job.id}`}>
                      <IconButton
                        size="small"
                        sx={{
                          border: '1px solid #f1f3f5',
                          borderRadius: '12px',
                          '&:hover': { backgroundColor: '#f1f3f5' }
                        }}
                      >
                        <Eye size={15} />
                      </IconButton>
                    </Link>
                  </Tooltip>

                  <Tooltip title="Thao tác" arrow>
                    <IconButton
                      onClick={(e) => handleOpenMenu(e, job.id)}
                      size="small"
                      sx={{
                        border: '1px solid #f1f3f5',
                        borderRadius: '12px',
                        '&:hover': { backgroundColor: '#f1f3f5' }
                      }}
                    >
                      <MoreVertical size={15} />
                    </IconButton>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* 3-Dot Dropdown Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '16px',
            border: '1px solid #f1f3f5',
            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            minWidth: 140,
            p: 0.5,
          }
        }}
      >
        {activeJob && (
          <div>
            <MenuItem
              component={Link}
              to={`/viec-lam/sua/${activeJob.id}`}
              onClick={handleCloseMenu}
              sx={{ fontSize: '12px', fontWeight: 'bold', color: '#475569', py: 1, borderRadius: '8px', gap: 1 }}
            >
              <Edit3 size={14} className="text-gray-500" /> Sửa tin
            </MenuItem>
            <MenuItem
              onClick={() => {
                onDelete(activeJob.id);
                handleCloseMenu();
              }}
              sx={{ fontSize: '12px', fontWeight: 'bold', color: '#ef4444', py: 1, borderRadius: '8px', gap: 1, '&:hover': { backgroundColor: '#fef2f2' } }}
            >
              <Trash2 size={14} className="text-[#ef4444]" /> Xóa tin
            </MenuItem>
          </div>
        )}
      </Menu>
    </TableContainer>
  );
}
